<template>
  <div v-if="fullMode" style="position: fixed; right: 15px; z-index: 8999;" :style="computedStyle">
    <div  style="width: 480px !important; margin-right: 20px;">
      <div v-show="shouldShowCarfaxHeader" class="box" style=" background-color: #f0f8f3;">
        <div class="columns">
          <div class="column is-narrow" style="display: flex;flex-direction: row;">
            <div style="display: flex; flex-direction: row;">
              <img src="@/assets/static/carfax/CARFAX Connect Logo.svg" style="width:78px; height:22px; margin-top: 5px; margin-right: 5px;" >
              <b-icon :class="carfaxIconStatusClass"></b-icon>
            </div>
            <span style="margin-top: 0px; margin-left: 10px;" :style="messageStyling">{{ carfaxProgressStatusMessage }}</span>
          </div>
          <div class="column ellipsis">
          </div>
          <div class="column is-narrow">
            <span title="Close CARFAX processing progress tracking." class="delete is-medium has-background-grey-light" @click="closeCarfaxControl"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="modalMode" class="transparent-floating-detail" style="width: 100% !important; top: 60px !important;" :style="computedStyle">
    <div v-show="shouldShowCarfaxHeader" class="box" style=" background-color: #f0f8f3;">
      <div class="columns">
        <div class="column is-narrow" style="display: flex;flex-direction: row;">
          <div style="display: flex; flex-direction: row;">
            <img src="@/assets/static/carfax/CARFAX Connect Logo.svg" style="width:78px; height:22px; margin-top: 5px; margin-right: 5px;" >
            <b-icon :class="carfaxIconStatusClass"></b-icon>
          </div>
          <span style="margin-top: 0px; margin-left: 10px;" :style="messageStyling">{{ carfaxProgressStatusMessage }}</span>
        </div>
        <div class="column ellipsis">
        </div>
        <div class="column is-narrow">
          <span title="Close CARFAX processing progress tracking." class="delete is-medium has-background-grey-light" @click="closeCarfaxControl"></span>
        </div>
      </div>
    </div>
  </div>
  <!-- ⚠ A BOUNDED WIDTH, not a viewport percentage. This is position:fixed, so "width: 92%" was 92%
       of the VIEWPORT rather than of the content area - anchored right:20px it put the left edge at
       ~131px on a 1889px screen, well inside the 250px sidebar, so the green bar covered the menu.
       Measured 21 Sep: header left=131, sidebar right=266.

       480px matches the other two render modes, and the message inside is capped at 210px anyway, so
       the extra width bought nothing. max-width keeps it inside the viewport on a narrow screen
       without hard-coding the sidebar width, which would be wrong once the sidebar is collapsed. -->
  <!-- ⚠ panelBoundedStyle overrides the 480px when this render sits INSIDE a quick-view. The
       480px stays as the standalone default: ViewInventory and ViewDeal render this same branch as
       full pages too, where there is no panel to measure and the old width is still right. -->
  <div v-else style="width: 480px !important; max-width: calc(100vw - 40px); position: fixed; right: 20px; z-index: 8999;"  :style="panelBoundedStyle">
    <div v-show="shouldShowCarfaxHeader" class="box" style=" background-color: #f0f8f3;">
      <div class="columns">
        <div class="column is-narrow" style="display: flex; flex-direction: row; align-items: center;">
          <div style="display: flex; flex-direction: row;">
            <img src="@/assets/static/carfax/CARFAX Connect Logo.svg" style="width:78px; height:22px; margin-top: 5px; margin-right: 5px;" >
            <b-icon :class="carfaxIconStatusClass"></b-icon>
          </div>
          <span style="margin-top: 0px; max-width: 210px;" v-tooltip="carfaxProgressStatusMessage" :style="messageStyling">{{ carfaxProgressStatusMessage }}</span>
        </div>
        <div class="column ellipsis">
        </div>
        <div class="column is-narrow">
          <span title="Close CARFAX processing progress tracking." class="delete is-medium has-background-grey-light" @click="closeCarfaxControl"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import eventBus from '@/eventBus'
const mapUserGetters = createNamespacedHelpers('user').mapGetters

/*
 * The progress stages an operator sees, and which CARFAX statuses map onto each.
 *
 * ⚠ TEN STATUSES, FIVE STAGES. The server still reports ten - measured 20 Sep, one refresh, one
 * tracker row, ten sends - but four of them describe a two-call design that no longer exists:
 * CarfaxBasicInfoRequested and CarfaxHistoryRequested are emitted three lines apart with no work
 * between them (CarFaxService.cs:1378/:1381; both extractions start together under Task.WhenAll), and
 * CarfaxBasicInfoCompleted / CarfaxHistoryRequestCompleted describe the same instant. Showing a
 * message per status reads as a flicker of notifications for one action.
 *
 * ⚠ COLLAPSED HERE RATHER THAN BY DELETING STATUSES. The enum is persisted on
 * CarfaxRatingProcessingStatusTracker and the rows are the audit trail of what CARFAX actually did;
 * the standalone history endpoint still sets the history statuses legitimately. This changes what the
 * operator is shown, not what is recorded.
 *
 * ⚠ IT ALSO FIXES THE ORDERING. The push handler delays each status by a per-category amount tuned
 * for the OLD sequential order - PDF 700ms against basic-info 800 and history 900 - so the PDF stages
 * now overtake the extraction stages they follow. Measured arrival: BasicInfoRequested ->
 * PdfGenerationRequested -> CarfaxHistoryRequested -> PdfGenerationCompleted. All four map to one
 * message, so the order stops mattering and the text can no longer appear to go backwards.
 */
const READING_THE_REPORT = 'Reading the CARFAX report.'
const REQUESTING_THE_REPORT = 'Requesting the CARFAX report.'

export const CARFAX_PROGRESS_STAGES = {
  CheckingUserSession: { rank: 1, message: 'Checking user CARFAX session status.' },
  UserSessionValid: { rank: 2, message: 'User session valid.' },
  CarfaxRatingRequestPending: { rank: 3, message: REQUESTING_THE_REPORT },
  CarfaxRatingUrlRequestComplete: { rank: 3, message: REQUESTING_THE_REPORT },
  PdfGenerationRequested: { rank: 4, message: READING_THE_REPORT },
  PdfGenerationCompleted: { rank: 4, message: READING_THE_REPORT },
  CarfaxBasicInfoRequested: { rank: 4, message: READING_THE_REPORT },
  CarfaxHistoryRequested: { rank: 4, message: READING_THE_REPORT },
  //⚠ BOTH are terminal. They report the same instant, and which one lands last depends on the delay
  //ladder (800 against 900), so neither can be relied on alone to close the box.
  CarfaxBasicInfoCompleted: { message: 'CARFAX report read.', terminal: true },
  CarfaxHistoryRequestCompleted: { message: 'CARFAX report read.', terminal: true },
  /*
   * ⚠ Failures are terminal too, which they were NOT before: only CarfaxHistoryRequestCompleted and
   * CarfaxRatingPaymentRequired ever started the close timer, so a failed run left its box on screen
   * until the operator dismissed it by hand.
   *
   * ⚠ A later terminal still replaces an earlier one - see applyCarfaxStatus - so a failed history
   * leg does not hide a successful basic-info leg finishing a moment later.
   */
  UserSessionInvalid: { message: 'User session validation check failed.', terminal: true },
  CarfaxRatingUrlError: { message: 'CARFAX request failed.', terminal: true },
  PdfGenerationFailed: { message: 'CARFAX vehicle report PDF generation failed.', terminal: true },
  CarfaxBasicInfoFailed: { message: 'Reading the CARFAX report failed.', terminal: true },
  CarfaxHistoryRequestFailed: { message: 'Reading the CARFAX report failed.', terminal: true },
  CarfaxRatingPaymentRequired: { message: 'CARFAX request failed, payment required.', terminal: true }
}

//Statuses that begin a run. Its own table because the stage map above is about what is SHOWN, and
//these four are about when the cycle's latches are released.
const CYCLE_START_STATUSES = {
  CheckingUserSession: true,
  UserSessionValid: true,
  UserSessionInvalid: true,
  CarfaxRatingRequestPending: true
}

export default {
  name: 'CarfaxProcessStatusHeader',
  props: {
    vinNo: {
      type: String,
      default: ''
    },
    fullMode: {
      type: Boolean,
      default: true
    },
    modalMode: {
      type: Boolean,
      default: false
    },
    inventoryId: {
      type: Number,
      default: null
    },
    leftMargin: {
      type: String,
      default: 'margin-left: 0px !important;'
    },
    messageStyling: {
      type: String,
      default: 'max-width: 210px;'
    },
    carfaxRequested: {
      type: Boolean,
      default: false
    },
    topPosition: {
      type: String,
      default: 'top: 75px;'
    },
    hideHeader: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      instanceName: this.$uuid.v4(),
      showCarfaxProgressStatus: false,
      carfaxProgressStatusMessage: '',
      carfaxIconStatusClass: '',
      intervalId: null,
      timerCount: 3,
      requestStatusValue: '',
      //⚠ The message WITHOUT the countdown clause. startTimer used to rebuild the whole sentence from
      //requestStatusValue with an else that said "detailed history summary request failed" for any
      //status it did not name - wrong wording the moment the terminal status changed.
      carfaxProgressBaseMessage: '',
      //Whether this cycle has reached an end state, so late stages cannot reopen the box.
      cycleTerminal: false,
      //How far this run has got. Measured live: the stages arrive badly out of order, so the rank -
      //not the arrival - decides what is shown. 0 means nothing shown yet.
      cycleRank: 0,
      userClosedForCurrentCycle: false,
      /*
       * ⚠ The quick-view panel's CONTENT box, or null when this header is not inside one. Measured
       * rather than assumed: .quickview-expanded-1/2/3 resize the panel to 25%, 35% or 50% of the
       * viewport, and the body's scrollbar takes a few more pixels, so no constant is right for long.
       */
      quickviewBounds: null,
      quickviewResizeObserver: null
    }
  },
  methods: {
    /*
     * ⚠ Measures the panel this header is rendered inside, so a position:fixed bar cannot spill out
     * of it. Anchored right:20px at a hard 480px, it was 25px WIDER than the 475px quick-view on the
     * left and 20px short on the right - the green bar overhung the inventory grid behind the panel.
     * Measured 22 Sep: header 1384..1864, panel 1409..1884, cards inside 1425..1863.
     *
     * ⚠ Bounds the CONTENT box, not the panel box, so the bar lines up with the cards beside it
     * rather than with the panel's outer edge. clientWidth excludes the scrollbar; the rect does not.
     *
     * ⚠ Returns null when there is no .quickview ancestor, which is what leaves every full-page host
     * on exactly the width it has today. No host passes anything - the DOM already knows.
     */
    measureQuickviewBounds: function () {
      const panel = this.$el && this.$el.closest ? this.$el.closest('.quickview') : null
      const body = panel ? panel.querySelector('.quickview-body') : null
      if (!body) {
        this.quickviewBounds = null
        return
      }

      const styling = window.getComputedStyle(body)
      const padLeft = parseFloat(styling.paddingLeft) || 0
      const padRight = parseFloat(styling.paddingRight) || 0
      const width = body.clientWidth - padLeft - padRight
      if (!(width > 0)) {
        this.quickviewBounds = null
        return
      }

      /*
       * ⚠ `right` is measured from the PANEL, not the viewport, because the panel is this bar's
       * containing block: .quickview carries will-change:transform for its slide-in, and a
       * transform-ish ancestor captures position:fixed descendants. That is also why the old 480px
       * overhung - it was simply wider than the 475px box it was being positioned inside.
       *
       * ⚠ Tested rather than assumed. Drop that will-change and fixed anchors to the viewport
       * again, and a panel-relative offset would put the bar in the wrong place entirely.
       */
      const panelStyling = window.getComputedStyle(panel)
      const panelIsContainingBlock = panelStyling.transform !== 'none' ||
        panelStyling.filter !== 'none' ||
        panelStyling.perspective !== 'none' ||
        /transform|filter|perspective/.test(panelStyling.willChange || '')
      const outerRight = panelIsContainingBlock ? panel.getBoundingClientRect().right : window.innerWidth

      const left = body.getBoundingClientRect().left + padLeft
      this.quickviewBounds = { width: width, right: Math.max(0, outerRight - (left + width)) }
    },
    /*
     * ⚠ A ResizeObserver rather than a window resize listener. The operator expands the panel with
     * the quick-view buttons, which swap a class and fire NO resize event - the bar would keep the
     * old width until something else re-rendered it.
     */
    watchQuickviewBounds: function () {
      this.measureQuickviewBounds()

      const panel = this.$el && this.$el.closest ? this.$el.closest('.quickview') : null
      const body = panel ? panel.querySelector('.quickview-body') : null
      if (!body || typeof ResizeObserver === 'undefined') return

      this.quickviewResizeObserver = new ResizeObserver(() => { this.measureQuickviewBounds() })
      this.quickviewResizeObserver.observe(body)
    },
    /*
     * ⚠ THE BOX OPENS ONLY FOR A STAGE THAT IS ACTUALLY SHOWN. This used to set
     * showCarfaxProgressStatus on every matching packet, before deciding whether the packet had
     * anything to say - so a late stage RE-OPENED a box whose countdown had already closed it, and
     * because the countdown had finished there was nothing left to close it again. It sat there
     * showing a stale sentence until dismissed by hand.
     *
     * Observed on 20 Sep: the run ended at 25.4s and the box closed at 28.4s, then
     * CarfaxHistoryRequested and CarfaxHistoryRequestCompleted arrived at 40.5s and re-opened it.
     *
     * The icon is gated with it - otherwise a late "requested" stage spins the icon on a finished run.
     */
    onCarfaxStatusChanged: function (notification) {
      if (this.vinNo !== notification.data.vinNo) return

      this.requestStatusValue = notification.data.requestStatus
      if (!this.setCarfaxStatusInfoMessage(notification.data.requestStatus)) return

      this.showCarfaxProgressStatus = true
      this.setCommissionIconClassByStatus(notification.data.requestStatus)
    },
    /*
     * One status in, at most one visible change out.
     *
     * ⚠ REPEATS ARE SUPPRESSED. Four statuses map to "Reading the CARFAX report." and two to the
     * requesting stage; without this the operator still sees the same sentence rewritten several times,
     * which is the flicker this exists to remove.
     *
     * ⚠ THE TERMINAL LATCH. showCarfaxProgressStatus used to be set on every matching packet with no
     * end-state guard, so a late stage - and they do arrive late and out of order, see the stage map -
     * could reopen a box that had already closed. After an end state only another end state is
     * accepted, which also lets a successful leg replace a failed one finishing a moment earlier.
     */
    setCarfaxStatusInfoMessage: function (requestStatus) {
      this.resetCycleStatus(requestStatus)

      const stage = CARFAX_PROGRESS_STAGES[requestStatus]
      //⚠ An unknown status leaves the box exactly as it was. The old switch fell through and blanked
      //the message, showing an empty container.
      if (!stage) return false

      if (stage.terminal) {
        /*
         * ⚠ THE SECOND "done" MUST BE SILENT. CarfaxBasicInfoCompleted and CarfaxHistoryRequestCompleted
         * carry the SAME sentence and both are terminal, so accepting the second restarted the countdown
         * at 3 a moment after it began - a visible jump. One that CHANGES the text is still heard, so a
         * failed leg cannot hide a successful one finishing just after it.
         */
        if (this.cycleTerminal && stage.message === this.carfaxProgressBaseMessage) return false
        return this.showStage(stage, true)
      }

      //Once a run has ended, only another end state is heard.
      if (this.cycleTerminal) return false
      /*
       * ⚠ RANK, NOT ARRIVAL. Measured live, 20 Sep: five stages landed within 3ms in the order
       * Pending, PdfGenerationRequested, UserSessionValid, CheckingUserSession, UrlRequestComplete. The
       * push handler schedules each status as its own Hangfire job with a per-category delay (session
       * 500ms, rating-url 600, PDF 700, basic-info 800, history 900) measured from its OWN enqueue
       * instant, and the gaps between categories are smaller than the real step latency - so the ladder
       * cannot order anything. Showing whatever arrived last made the box read backwards.
       *
       * A run only ever moves forward. Anything at or below where it already is has nothing to add.
       */
      if (stage.rank <= this.cycleRank) return false
      return this.showStage(stage, false)
    },
    showStage: function (stage, terminal) {
      this.carfaxProgressBaseMessage = stage.message
      this.carfaxProgressStatusMessage = stage.message
      /*
       * ⚠ RANK IS A NON-TERMINAL IDEA, so only the non-terminal path records it. Terminal stages carry
       * no rank - they are ordered by the terminal latch and by message, not by rank - and writing it
       * here unconditionally put `undefined` into cycleRank the moment a run ended.
       *
       * Unobservable today: after an end state the cycleTerminal guard rejects every non-terminal
       * status before the rank comparison, and resetCycleStatus zeroes the field when the next run
       * starts. It was still worth removing, because the obvious future change - ordering terminals by
       * rank the way the non-terminal stages are ordered - would have read those undefineds silently.
       */
      if (!terminal) {
        this.cycleRank = stage.rank
        return true
      }

      this.cycleTerminal = true
      //⚠ restartTimer, not startTimer: a replacing end state must replace the first one's countdown
      //rather than be ignored by the "prevent multiple intervals" guard.
      this.restartTimer()
      return true
    },
    closeCarfaxControl: function () {
      this.showCarfaxProgressStatus = false
      this.userClosedForCurrentCycle = true
      //⚠ resetTimer was only ever reached at timerCount === 0, so dismissing the box mid-countdown
      //left the count where it stopped and the next run closed itself after whatever was left of it.
      this.resetTimer()
    },
    setCommissionIconClassByStatus: function (requestStatus) {
      var result = 'fal fa-sync has-text-black'
      if (requestStatus) {
        switch (requestStatus) {
          case 'CheckingUserSession':
          case 'CarfaxRatingRequestPending':
          case 'PdfGenerationRequested':
          case 'CarfaxBasicInfoRequested':
          case 'CarfaxHistoryRequested':
            result = 'fal fa-sync has-text-orange fa-spin'
            break
          case 'UserSessionValid':
          case 'CarfaxRatingUrlRequestComplete':
          case 'PdfGenerationCompleted':
          case 'CarfaxBasicInfoCompleted':
          case 'CarfaxHistoryRequestCompleted':
            result = 'fal fa-check has-text-success'
            break
          case 'UserSessionInvalid':
          case 'CarfaxRatingUrlError':
          case 'PdfGenerationFailed':
          case 'CarfaxBasicInfoFailed':
          case 'CarfaxHistoryRequestFailed':
          case 'CarfaxRatingPaymentRequired':
            result = 'fal fa-ban has-text-danger'
            break
        }
      }
      this.carfaxIconStatusClass = result
    },
    /*
     * ⚠ The countdown is composed from carfaxProgressBaseMessage, not rebuilt from the status. The
     * old version branched on requestStatusValue and fell through to "CARFAX detailed history summary
     * request failed" for anything it did not name - so every terminal status other than the three it
     * listed would have announced a history failure.
     */
    countdownMessage: function () {
      return this.carfaxProgressBaseMessage + ' This container will automatically close in ' +
        this.timerCount + ' seconds.'
    },
    restartTimer() {
      this.resetTimer()
      this.carfaxProgressStatusMessage = this.countdownMessage()
      this.startTimer()
    },
    startTimer() {
      if (this.intervalId === null) { // Prevent multiple intervals
        let that = this
        that.intervalId = setInterval(() => {
          that.timerCount--
          that.carfaxProgressStatusMessage = that.countdownMessage()
          if (that.timerCount === 0) {
            that.hideCarfaxStatusControl()
            that.resetTimer()
          }
        }, 1000); // Update every 1000 milliseconds (1 second)
      }
    },
    stopTimer() {
      clearInterval(this.intervalId)
      this.intervalId = null
    },
    resetTimer() {
      this.stopTimer()
      this.timerCount = 3
    },
    hideCarfaxStatusControl: function () {
      this.showCarfaxProgressStatus = false
    },
    /*
     * A new run of the pipeline re-arms everything the last one latched.
     *
     * ⚠ IT NOW CLEARS THE TERMINAL LATCH TOO, and it must. cycleTerminal stops late stages reopening
     * a closed box, so without clearing it here the SECOND CARFAX request of a page's life would be
     * swallowed whole - every stage refused by the guard, no box, no progress shown at all.
     *
     * ⚠ The four statuses are the ones that can legitimately OPEN a run. The header is mounted on
     * hosts that never see CheckingUserSession because the session was already known good.
     */
    resetCycleStatus: function (requestStatus) {
      if (!requestStatus || CYCLE_START_STATUSES[requestStatus] !== true) return
      /*
       * ⚠ ONLY BETWEEN RUNS. A cycle-start status also arrives LATE inside a live run - measured, see
       * above - and re-arming on it wiped the progress and replayed the earlier stages, which is the
       * backwards flicker the operator reports. A genuinely new run can only begin once this one has
       * ended, or once the operator has dismissed it.
       */
      if (!this.cycleTerminal && !this.userClosedForCurrentCycle && this.cycleRank !== 0) return

      this.userClosedForCurrentCycle = false
      this.cycleTerminal = false
      this.cycleRank = 0
      this.carfaxProgressBaseMessage = ''
      this.resetTimer()
    },
    carfaxRequestCancelled: function () {
      this.showCarfaxProgressStatus = false
    }
  },
  computed: {
    ...mapUserGetters(['currentUser']),
    computedStyle: function () {
      let result = this.leftMargin
      if (this.topPosition) {
        result = result + ' ' + this.topPosition
      }
      if (this.showCarfaxProgressStatus === false) {
         result = result + 'max-height: 0px !important; display: none;'
      }
      return result
    },
    /*
     * ⚠ computedStyle plus the panel bounds, and ONLY for the standalone branch. The modal branch is
     * deliberately left alone: it is already width:100% of a dialog, and an agreement modal opened
     * from the quick-view would otherwise shrink its header to the panel's width.
     */
    panelBoundedStyle: function () {
      const base = this.computedStyle
      const bounds = this.quickviewBounds
      if (!bounds) return base

      //⚠ !important because the static attribute's 480px carries it too - without it the narrower
      //value loses and nothing appears to change.
      return base + ' width: ' + bounds.width + 'px !important; right: ' + bounds.right + 'px !important;'
    },
    hasCarfaxTesterRole: function () {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.find((x) => { return x.name.toLowerCase().includes('carfax tester') }) !== undefined
      }
      return true
    },
    currentUserHasCarfaxSession: function () {
      if (this.currentUser && this.currentUser.carFaxSession && this.currentUser.carFaxSession.hasValidSession) {
        return true
      }
      return false
    },
    shouldShowCarfaxHeader: function() {
      return this.currentUserHasCarfaxSession && 
            !this.hasCarfaxTesterRole && 
            this.showCarfaxProgressStatus && 
            !this.hideHeader && 
            !this.userClosedForCurrentCycle
    }
  },
  watch: {
    carfaxRequested: function (newValue, oldValue) {
      if (!oldValue && newValue || oldValue === false && newValue && newValue === true) {
        this.showCarfaxProgressStatus = true
        this.carfaxProgressStatusMessage = 'Checking user CARFAX session status.'
        this.setCommissionIconClassByStatus('CheckingUserSession')
      }
    }
  },
  mounted: function () {
    eventBus.$on('carfax-rating-processing-status-tracker', this.onCarfaxStatusChanged)
    eventBus.$on('carfax-request-cancelled', this.carfaxRequestCancelled)
    this.watchQuickviewBounds()
  },
  beforeDestroy () {
    eventBus.$off('carfax-rating-processing-status-tracker', this.onCarfaxStatusChanged)
    eventBus.$off('carfax-request-cancelled', this.carfaxRequestCancelled)
    if (this.quickviewResizeObserver) {
      this.quickviewResizeObserver.disconnect()
      this.quickviewResizeObserver = null
    }
    this.stopTimer()
  }
}
</script>

<style scoped>

.xp-icon-minus {
  font-size:0.75em;
  vertical-align:middle;
}

.xp-icon-plus {
  font-size:0.75em;
  vertical-align:middle;
}

.ellipsis {
  width:auto;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

</style>
